import { useState } from 'react';

import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Hide,
  Icon,
  Image,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Show,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import ArrowDown from 'images/icons/arrow-down-purple.svg';
import ArrowRight from 'images/icons/arrow-right-grey.svg';
import ArrowIcon from 'images/icons/arrow-right.svg';
import ArrowUp from 'images/icons/arrow-up-grey.svg';

import { Button } from '@/components/atoms';
import { StrapiImage } from '@/components/atoms';
import { GetMenuQuery } from '@/graphql/generated';
import { CONTENT_MAX_WIDTH_PX, HEADER_HEIGHT } from '@/utils/constants';
import { GeneralPath } from '@/utils/models';

import { HamburgerIcon } from './MobileHeaderContent/HamburgerIcon/HamburgerIcon';

type Props = {
  menu: GetMenuQuery['menu'];
};

const expandableCategories = ['services', 'industries'];

export const Header = ({ menu }: Props) => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const data = menu?.data?.attributes;
  const [openMobileCategories, setOpenMobileCategories] = useState<Record<string, boolean>>({});

  if (!data?.menuItem) return null;

  const toggleMobileCategory = (title: string) => {
    setOpenMobileCategories((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <>
      {/* HEADER - desktop + hamburger */}
      <Flex
        as="header"
        height={HEADER_HEIGHT}
        position="fixed"
        alignItems="center"
        justifyContent="center"
        width="100%"
        top="0"
        left="0"
        zIndex="sticky"
        backgroundColor="beige.50"
        borderBottom="1px solid"
        borderBottomColor="purple.100"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          w="full"
          maxW={CONTENT_MAX_WIDTH_PX}
          mx={{ base: 5, xl: 20 }}
        >
          {/* Logo */}
          {data.headerLogo?.data?.attributes?.url && (
            <Box w="140px" h="auto">
              <Link href={GeneralPath.HOME}>
                <StrapiImage image={data.headerLogo} alt="startup house warsaw logo" />
              </Link>
            </Box>
          )}

          {/* Hamburger icon */}
          <Show below="md">
            <Button
              leftIcon={<HamburgerIcon isMenuOpen={isOpen} />}
              onClick={onToggle}
              variant="navbar"
              minW="24px"
              w="24px"
              h="24px"
              ml="auto"
              aria-label="Open menu"
            />
          </Show>

          {/* Desktop Menu */}
          <HStack spacing={8} display={{ base: 'none', md: 'flex' }}>
            {data.menuItem.map((item, index) => {
              const category = item?.menuCategory;
              if (!category) return null;

              const title = category.title?.toLowerCase() || '';
              const subcategories = category.menuSubcategories?.filter(Boolean) || [];

              return (
                <Popover trigger="hover" placement="bottom-start" offset={[-90, 28]} key={`menu-${index}`}>
                  {({ isOpen }) => (
                    <>
                      {title === 'industries' || title === 'services' ? (
                        <PopoverTrigger>
                          <Box
                            as="button"
                            display="inline-flex"
                            alignItems="center"
                            bg="transparent"
                            border="none"
                            cursor="pointer"
                          >
                            <Text
                              variant="text-md/lineHeight-6/font-medium"
                              color="purple.500"
                              _hover={{ textDecoration: 'underline' }}
                            >
                              {category.title}
                            </Text>
                            <Icon
                              as={isOpen ? ArrowUp : ArrowDown}
                              width="16px"
                              height="16px"
                              aria-hidden="true"
                              ml={2}
                            />
                          </Box>
                        </PopoverTrigger>
                      ) : (
                        <Link href={category.url ?? undefined} color="purple.500">
                          {category.title}
                        </Link>
                      )}

                      {title === 'industries' && (
                        <PopoverContent bg="white" borderRadius="0" minW="440px" maxH="90vh" overflowY="auto">
                          <Flex wrap="wrap" p={5}>
                            {subcategories.map((sub, subIndex) => {
                              if (!sub) return null;

                              return (
                                <Box
                                  key={`submenu-${subIndex}`}
                                  w="full"
                                  p={5}
                                  borderRadius="8px"
                                  _hover={{ bg: '#F3F0FA', cursor: 'pointer' }}
                                >
                                  <VStack align="start" spacing={2}>
                                    <Link href={sub.url ?? undefined} w="100%">
                                      <Flex align="center" w="full" justify="space-between" gap={4}>
                                        <HStack align="center" spacing={4} flex="1">
                                          {sub.icon?.data?.attributes?.url && (
                                            <Image
                                              src={sub.icon.data.attributes.url}
                                              alt={sub.title}
                                              boxSize="32px"
                                              objectFit="contain"
                                            />
                                          )}
                                          <VStack align="start" spacing={0}>
                                            <Text color="purple.500" variant="text-xl/lineHeight-7/font-semibold">
                                              {sub.title}
                                            </Text>

                                            <Text variant="text-xs/lineHeight-4/font-normal/0.75rem">
                                              {sub.description}
                                            </Text>
                                          </VStack>
                                        </HStack>
                                        <Icon as={ArrowRight} width="24px" height="24px" aria-hidden="true" />
                                      </Flex>
                                    </Link>
                                  </VStack>
                                </Box>
                              );
                            })}
                          </Flex>
                          <Box bg="purple.600" px={10} py="30px">
                            <Text variant="text-xl/lineHeight-7/font-normal" color="white" pb={6}>
                              {data.desktopCtaHeading || 'Sprawdź nas!'}
                            </Text>
                            <Button
                              variant="light"
                              label={data.desktopButtonCaption || "Let's talk"}
                              rightIcon={<ArrowIcon width="12px" />}
                              w={{ base: '100%', md: 'max-content' }}
                              size="lg"
                              url={GeneralPath.CONTACT}
                            />
                          </Box>
                        </PopoverContent>
                      )}

                      {title === 'services' && (
                        <PopoverContent
                          bg="white"
                          borderRadius="0"
                          maxW="container.xl"
                          w="100%"
                          maxH="90vh"
                          overflowY="auto"
                        >
                          <Box
                            display="grid"
                            gap={8}
                            gridTemplateColumns={{
                              base: '1fr',
                              md: 'repeat(auto-fit, minmax(240px, 1fr))',
                              xl: 'repeat(3, 360px)',
                            }}
                            justifyContent={{ md: 'center', xl: 'start' }}
                          >
                            {subcategories.map((sub, subIndex) => {
                              if (!sub) return null;
                              return (
                                <Box key={`submenu-${subIndex}`} p={10}>
                                  <VStack align="start" justify="stretch" spacing={4}>
                                    <Link href={sub.url ?? undefined} w="100%">
                                      <Flex align="center" justify="space-between" w="full">
                                        <Text fontSize="lg" lineHeight="26px" color="purple.500" fontWeight="500">
                                          {sub.title}
                                        </Text>
                                        <Icon as={ArrowRight} width="24px" height="24px" aria-hidden="true" ml={2} />
                                      </Flex>
                                    </Link>
                                    {sub.menuLinks?.map((link, linkIndex) => {
                                      if (!link) return null;
                                      return (
                                        <Link
                                          key={`link-${linkIndex}`}
                                          href={link.url ?? undefined}
                                          fontSize="sm"
                                          w="100%"
                                        >
                                          {link.title}
                                        </Link>
                                      );
                                    })}
                                  </VStack>
                                </Box>
                              );
                            })}
                          </Box>

                          {/* CTA */}
                          <Flex
                            mt={10}
                            bg="purple.600"
                            px={10}
                            py="30px"
                            align="center"
                            justify="space-between"
                            flexWrap="wrap"
                            gap={6}
                          >
                            <Text variant="text-xl/lineHeight-7/font-normal" color="white" flexShrink={1} maxW="500px">
                              {data.desktopCtaHeading || 'Sprawdź nas!'}
                            </Text>
                            <Button
                              variant="light"
                              label={data.desktopButtonCaption || "Let's talk"}
                              rightIcon={<ArrowIcon width="12px" />}
                              w={{ base: '100%', md: '178px' }}
                              size="md"
                              url={GeneralPath.CONTACT}
                            />
                          </Flex>
                        </PopoverContent>
                      )}
                    </>
                  )}
                </Popover>
              );
            })}
          </HStack>
          <Hide below="md">
            <Button
              variant="light"
              label={"Let's talk"}
              width="98px"
              fontSize="component-paragraph-headline/md"
              url={GeneralPath.CONTACT}
            />
          </Hide>
        </Flex>
      </Flex>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="top" onClose={onClose} size="full">
        <DrawerOverlay />
        <DrawerContent bg="white" pt="40px" px={0} py={0} h="100%" overflowY="auto">
          <DrawerBody
            px={0}
            py={0}
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            overflowY="auto"
          >
            <VStack align="stretch" px={6} my={10}>
              <Flex justifyContent="start" alignItems="center" w="full" h="40px" gap={8} mb={10}>
                {/* Logo */}
                {data.headerLogo?.data?.attributes?.url && (
                  <Box w="140px" h="auto">
                    <Link href={GeneralPath.HOME}>
                      <StrapiImage image={data.headerLogo} alt="startup house logo" />
                    </Link>
                  </Box>
                )}

                {/* Hamburger icon */}
                <Button
                  leftIcon={<HamburgerIcon isMenuOpen={isOpen} />}
                  onClick={onToggle}
                  variant="navbar"
                  minW="24px"
                  w="24px"
                  h="24px"
                  ml="auto"
                  aria-label="Open menu"
                />
              </Flex>
              {data.menuItem.map((item, index) => {
                const category = item?.menuCategory;
                if (!category) return null;

                const title = category.title?.toLowerCase() || '';
                const subcategories = category.menuSubcategories?.filter(Boolean) || [];
                const isExpandable = expandableCategories.includes(title);
                const isOpenCategory = openMobileCategories[title] || false;

                if (isExpandable) {
                  return (
                    <Box key={`mobile-category-${index}`} w="full">
                      <Flex
                        justify="start"
                        align="center"
                        cursor="pointer"
                        onClick={() => toggleMobileCategory(title)}
                        py="14px"
                      >
                        <Text variant="text-2xl/lineHeight-8/font-bold" color="purple.500">
                          {category.title}
                        </Text>
                        <Icon
                          as={isOpenCategory ? ArrowUp : ArrowDown}
                          w="24px"
                          h="24px"
                          aria-label={isOpenCategory ? 'collapse' : 'expand'}
                          ml={2}
                        />
                      </Flex>

                      <Collapse in={isOpenCategory} animateOpacity>
                        {subcategories.map((sub, i) => {
                          if (!sub) return null;
                          return (
                            <Box key={`mobile-sub-${i}`} py={4}>
                              <Link href={sub.url ?? undefined} w="100%">
                                <Flex align="center" justify="space-between">
                                  <Text variant="text-lg/lineHeight-7/font-semibold" color="purple.500">
                                    {sub.title}
                                  </Text>
                                  <Icon as={ArrowRight} w="24px" h="24px" />
                                </Flex>
                              </Link>
                              <HStack spacing={2} wrap="wrap" mt={2}>
                                {sub.menuLinks?.map((link, linkIndex) => {
                                  if (!link) return null;

                                  return (
                                    <Link key={`mobile-link-${linkIndex}`} href={link.url ?? undefined}>
                                      <Text
                                        variant="text-sm/lineHeight-5/font-normal"
                                        color="purple.400"
                                        _hover={{ text: 'underline' }}
                                      >
                                        {link.title}
                                      </Text>
                                    </Link>
                                  );
                                })}
                              </HStack>
                            </Box>
                          );
                        })}
                      </Collapse>
                    </Box>
                  );
                }

                // Dla innych kategorii bez rozwijania po prostu wypisujemy tytuł i linki
                return (
                  <Box key={`mobile-category-${index}`} w="full" py="14px">
                    <Link href={category.url ?? undefined}>
                      <Text variant="text-2xl/lineHeight-8/font-bold" color="purple.500">
                        {category.title}
                      </Text>
                    </Link>
                  </Box>
                );
              })}
            </VStack>
            <Box w="full" mt={6} bg="purple.600" p={6}>
              <Text color="white" mb={4}>
                {data.desktopCtaHeading || 'Accelerate your software development process.'}
              </Text>
              <Button
                label={data.desktopButtonCaption || 'Contact us'}
                url={GeneralPath.CONTACT}
                rightIcon={<ArrowIcon />}
                variant="light"
                w="200px"
              />
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
